import React, { useContext, useEffect, useState, useMemo } from "react";
import "./PagosYReten.css";
import {
  deleteAndGetPagosByNroCtaFromSoftland,
  getPagosNotAnnulledByNroCta,
} from "../../api/pagosYRetenciones";
import authContext from "../../context/authentication/authContex";
import proveedoresContext from "../../context/proveedores/proveedoresContext.js";
import SelectList from "../../components/SelectList/SelectList.js";
import PagosYRetencionesList from "../../components/pagosYretencionesList/PagosYRetencionesList";
import { getDateStringYYYYMMDD } from "../../utils/formatDate.js";

//Permisos
import { ACTUALIZAR_MOVIMIENTOS_PROVEEDOR } from "../../utils/permisos";
import Swal from "sweetalert2";

const PagosYReten = () => {
  const aContext = useContext(authContext);
  const { usuario, globalSettings } = aContext;

  const provContext = useContext(proveedoresContext);
  const { proveedores, actualizarProveedores } = provContext;

  const [filtros, setFiltros] = useState({
    fchdes: "1980-01-01",
    fchhas: getDateStringYYYYMMDD(),
  });
  const [pagos, setPagos] = useState([]);
  const [provider, setProvider] = useState({});
  const [empresa, setEmpresa] = useState(null);
  const listHeight = useMemo(() => window.innerHeight - 280, []);

  const isAdminOrControl = usuario.userType.some((item) =>
    ACTUALIZAR_MOVIMIENTOS_PROVEEDOR.includes(item.typeName)
  );
  const isProvUser = usuario.userType.some((item) => item.typeName === "PROV");

  useEffect(() => {
    actualizarProveedores();
  }, []);

  const empresasOptions = useMemo(() => {
    const receptoras = globalSettings?.empresasReceptoras || [];
    if (isAdminOrControl) {
      return receptoras.map((e) => ({ value: e.companyName, label: e.razonSocial }));
    }
    if (isProvUser) {
      const companyNamesDelProv = new Set(
        proveedores
          .filter((p) => p.cuit === usuario.cuit && p.companyName)
          .map((p) => p.companyName)
      );
      return receptoras
        .filter((e) => companyNamesDelProv.has(e.companyName))
        .map((e) => ({ value: e.companyName, label: e.razonSocial }));
    }
    return [];
  }, [globalSettings, proveedores, isAdminOrControl, isProvUser, usuario.cuit]);

  const proveedoresFiltrados = useMemo(() => {
    if (!empresa) return proveedores;
    return proveedores.filter((p) => p.companyName === empresa);
  }, [proveedores, empresa]);

  const handleChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  const handleChangeEmpresa = async (value) => {
    const selectedEmpresa = value.value;
    setEmpresa(selectedEmpresa);
    setProvider({});
    setPagos([]);

    if (isProvUser) {
      const matchingProvider = proveedores.find(
        (p) => p.cuit === usuario.cuit && p.companyName === selectedEmpresa
      );
      if (matchingProvider) {
        setProvider(matchingProvider);
        const resp = await getPagosNotAnnulledByNroCta(matchingProvider.nroCta, matchingProvider.companyName);
        setPagos(resp.data.data);
      }
    }
  };

  const handleChangeProveedor = async (value) => {
    const proveedorSeleccionado = proveedoresFiltrados.find(
      (p) =>
        `${p.nroCta}`.replace(/\s+/g, "") ===
        value.value.split(" ~ ")[0].replace(/\s+/g, "")
    );

    if (proveedorSeleccionado !== undefined) {
      setProvider(proveedorSeleccionado);
    }

    const resp = await getPagosNotAnnulledByNroCta(proveedorSeleccionado.nroCta, proveedorSeleccionado.companyName);
    setPagos(resp.data.data);
  };

  const actualizarMovimientosSegunProveedor = async () => {
    Swal.fire({
      title: `Desea actualizar los movimientos del proveedor ${provider.nroCta} - ${provider.nombre}?`,
      text: "Se traeran los datos de Softland para sobreescribir los pagos y retenciones del proveedor seleccionado.",
      showCancelButton: true,
      confirmButtonText: "Actualizar",
      cancelButtonText: "No, cancelar",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        return await deleteAndGetPagosByNroCtaFromSoftland(provider.nroCta,
          provider.companyName)
          .then((response) => {
            if (!response.status) {
              throw new Error(response.msg);
            }
            return response;
          })
          .catch((error) => {
            Swal.showValidationMessage(`Request failed: ${error}`);
          });
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        setPagos(result.value.data.data.pagosCreated);
        Swal.fire(
          "Actualizado!",
          "La base de datos ha sido actualizada correctamente.",
          "success"
        );
      }
    });
  };

  const selectorStyles = {
    additionalStyleDivSelectList: {
      alignItems: "center",
      gap: "0.5rem",
    },
    fontStyleTitle: {
      fontSize: "1.5rem",
    },
  };

  return (
    <div className="ctaCorrienteDiv">
      <div style={{ marginTop: "20px" }} className="asideFiltros">
        <SelectList
          text={"Empresa:"}
          options={empresasOptions}
          handleChange={handleChangeEmpresa}
          property={"Empresa"}
          valueAux={{
            label: empresa
              ? (globalSettings?.empresasReceptoras || []).find((e) => e.companyName === empresa)?.razonSocial || empresa
              : "Seleccione...",
            value: empresa || "",
          }}
          emptyInputs={[]}
          styles={selectorStyles}
        />
        <SelectList
          text={"Proveedor:"}
          options={proveedoresFiltrados.map((p) => ({
            value: `${p.nroCta}`,
            label: `${p.nroCta} - ${p.nombre}`,
          }))}
          handleChange={handleChangeProveedor}
          property={"Proveedor"}
          valueAux={{
            label:
              provider.nombre && provider.nroCta
                ? `${provider.nroCta} - ${provider.nombre}`
                : "Seleccione...",
            value: provider.nroCta || "",
          }}
          emptyInputs={[]}
          disabled={isProvUser}
          styles={selectorStyles}
        />
        <div className="inputGroup">
          <label>Fecha desde:</label>
          <input
            name="fchdes"
            type="date"
            defaultValue="1980-01-01"
            onChange={(e) => handleChange(e)}
          ></input>
        </div>
        <div className="inputGroup">
          <label>Fecha hasta:</label>
          <input
            name="fchhas"
            type="date"
            defaultValue={getDateStringYYYYMMDD()}
            onChange={(e) => handleChange(e)}
          ></input>
        </div>
      </div>
      <div className="resultadosDiv">
        {isAdminOrControl && (
          <button
            className={`actualizar-providers-btn ${
              !empresa || !provider?.nroCta ? "disabled-btn" : ""
            }`}
            style={{
              marginLeft: "auto",
              marginTop: "-55px",
              display: "block",
            }}
            disabled={!empresa || !provider?.nroCta}
            onClick={actualizarMovimientosSegunProveedor}
          >
            Actualizar Movimientos
          </button>
        )}
        <PagosYRetencionesList
          pagosList={pagos}
          fchdes={filtros.fchdes}
          fchhas={filtros.fchhas}
        />
      </div>
    </div>
  );
};

export default PagosYReten;
